import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { empAddressService } from '@/services/empAddressService'
import { empMedicalService } from '@/services/empMedicalService'
import { empMediaLinksService } from '@/services/empMediaLinksService'
import { empDocumentService } from '@/services/empDocumentService'
import { empSkillService } from '@/services/empSkillService'
import { empQualificationService } from '@/services/empQualificationService'
import { bankService } from '@/services/bankService'
import { contractService } from '@/services/contractService'
import { shiftService } from '@/services/shiftService'
import type {
  BankListItem,
  Contract,
  EmpAddressListItem,
  EmpDocumentListItem,
  EmpQualificationListItem,
  Employee,
  MediaLinksVm,
  MedicalVm,
  SkillListItem,
} from '@/api/types'
import { EmployeeProfileCards } from '@/modules/employees/components/EmployeeProfileCards'
import { EmployeeCvLayout } from '@/modules/employees/components/EmployeeCvLayout'
import { formatShiftClock, mergeContractSalary, pickActiveContractToday, settled } from '@/modules/employees/employeeDisplay'

export const EmployeesView: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [employee, setEmployee] = useState<Employee | null>(null)
  const [loading, setLoading] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [address, setAddress] = useState<EmpAddressListItem | undefined>()
  const [extraAddress, setExtraAddress] = useState<EmpAddressListItem | undefined>()
  const [medical, setMedical] = useState<MedicalVm | undefined>()
  const [media, setMedia] = useState<MediaLinksVm | undefined>()
  const [contract, setContract] = useState<Contract | null>(null)
  const [bank, setBank] = useState<BankListItem | undefined>()
  const [documents, setDocuments] = useState<EmpDocumentListItem[]>([])
  const [skills, setSkills] = useState<SkillListItem[]>([])
  const [qualifications, setQualifications] = useState<EmpQualificationListItem[]>([])
  const [shiftLabel, setShiftLabel] = useState('')
  const [maritalName, setMaritalName] = useState('')
  const [noticePeriodName, setNoticePeriodName] = useState('')
  const [statusName, setStatusName] = useState('')
  const [addressCountry, setAddressCountry] = useState('')
  const [addressCity, setAddressCity] = useState('')

  useEffect(() => {
    if (!branchId || !id) return
    const empId = Number(id)
    if (!empId) return

    const load = async () => {
      setLoading(true)
      try {
        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)

        const [
          addresses,
          medicals,
          mediaRows,
          docsRes,
          skillRes,
          qualRes,
          banks,
          contractsRes,
          maritalLookups,
          noticeLookups,
          statusLookups,
          deptLookups,
          jobLookups,
          genderLookups,
          countryLookups,
          cityLookups,
          professionLookups,
        ] = await Promise.all([
          settled(empAddressService.getAll(branchId, empId, 1, 20).then((r) => r.data || []), []),
          settled(empMedicalService.getAll(branchId, empId, 1, 20).then((r) => r.data || []), []),
          settled(empMediaLinksService.getAll(branchId, empId, 1, 10).then((r) => r.data || []), []),
          settled(empDocumentService.getList(branchId, empId, 1, 50), { data: [], totalRecords: 0, pageNumber: 1, pageSize: 50 }),
          settled(empSkillService.getAll(branchId, empId, 1, 100), { data: [], totalRecords: 0, pageNumber: 1, pageSize: 100 }),
          settled(empQualificationService.getAll(branchId, empId, 1, 50), { data: [], totalRecords: 0, pageNumber: 1, pageSize: 50 }),
          settled(bankService.getBanksByEmpId(empId), []),
          settled(contractService.getAllContracts(branchId, empId, 1, 100), { data: [], totalRecords: 0, pageNumber: 1, pageSize: 100 }),
          settled(employeesService.getMaritalStatusLookups(), []),
          settled(employeesService.getNoticePeriodLookups(), []),
          settled(employeesService.getStatusLookups(), []),
          settled(employeesService.getDepartmentLookups(), []),
          settled(employeesService.getJobLookups(), []),
          settled(employeesService.getGenderLookups(), []),
          settled(employeesService.getCountryLookups(), []),
          settled(employeesService.getCityLookups(), []),
          settled(employeesService.getProfessionIqamaLookups(), []),
        ])

        const firstAddress = addresses[0]
        setAddress(firstAddress)
        setExtraAddress(addresses[1])
        setMedical(medicals[0])
        setMedia(mediaRows[0])
        setDocuments(docsRes.data || [])
        setSkills(skillRes.data || [])
        setQualifications(qualRes.data || [])
        setBank(banks[0])

        const lookupName = (list: { id: number; lookupNameEn?: string; lookupNameAr?: string }[], id?: number) => {
          const item = list.find((x) => x.id === id)
          return item?.lookupNameEn || item?.lookupNameAr || ''
        }

        setEmployee({
          ...emp,
          departmentName: emp.departmentName || lookupName(deptLookups, emp.empDepartmetId),
          jobName: emp.jobName || lookupName(jobLookups, emp.jobId) || lookupName(professionLookups, emp.professionIqama),
          genderName: emp.genderName || lookupName(genderLookups, emp.empGenderId),
          nationalityName: emp.nationalityName || lookupName(countryLookups, emp.empNationalityId),
        })
        setMaritalName(lookupName(maritalLookups, emp.maritalStatus))
        setNoticePeriodName(lookupName(noticeLookups, emp.noticePeriod) || (emp.noticePeriod ? String(emp.noticePeriod) : ''))
        setStatusName(lookupName(statusLookups, emp.status))
        setAddressCountry(lookupName(countryLookups, firstAddress?.countryId))
        setAddressCity(lookupName(cityLookups, firstAddress?.cityId))

        const activeContract = pickActiveContractToday(contractsRes.data || [])
        const contractId = Number(activeContract?.id || activeContract?.contractId || 0)
        if (contractId > 0) {
          const full = await settled<Contract | null>(
            contractService.getContractById(contractId, branchId),
            null
          )
          const salaryRow = await settled(
            contractService.getSalaryByEmployeeId(empId, contractId),
            null
          )
          setContract(mergeContractSalary((full || activeContract) as Contract, salaryRow))
        } else {
          setContract(null)
        }

        const shiftId = Number((emp as Employee).shiftId || 0)
        if (shiftId > 0) {
          const shift = await settled(shiftService.getById(shiftId, branchId), null as any)
          if (shift) {
            const start = formatShiftClock(shift.shiftStartTime || shift.startTime)
            const end = formatShiftClock(shift.shiftEndTime || shift.endTime)
            const shiftName = shift.name || shift.shiftType || ''
            setShiftLabel([shiftName, start && end ? `${start} - ${end}` : ''].filter(Boolean).join('  ·  '))
          }
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('employees.failedToLoadEmployee'))
        navigate('/employees')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [branchId, id, navigate, t])

  useEffect(() => {
    if (!employee?.empCode && !employee?.id) return
    let cancelled = false
    const run = async () => {
      try {
        const QRCode = await import('qrcode')
        const payload = employee.empCode || String(employee.id)
        const dataUrl = await QRCode.toDataURL(payload, { margin: 1, width: 180 })
        if (!cancelled) setQrDataUrl(dataUrl)
      } catch {
        if (!cancelled) setQrDataUrl('')
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [employee?.empCode, employee?.id])

  const print = (mode: 'profile' | 'cv') => {
    document.documentElement.classList.remove('emp-print-profile', 'emp-print-cv')
    document.body.classList.remove('emp-print-profile', 'emp-print-cv')
    document.documentElement.classList.add(mode === 'cv' ? 'emp-print-cv' : 'emp-print-profile')
    document.body.classList.add(mode === 'cv' ? 'emp-print-cv' : 'emp-print-profile')

    let pageStyle = document.getElementById('emp-print-page-style') as HTMLStyleElement | null
    if (!pageStyle) {
      pageStyle = document.createElement('style')
      pageStyle.id = 'emp-print-page-style'
      document.head.appendChild(pageStyle)
    }
    pageStyle.textContent = mode === 'cv'
      ? '@page { size: A4; margin: 0; }'
      : '@page { size: A4; margin: 6mm; }'

    const sheet = document.querySelector('.employee-profile-page') as HTMLElement | null
    if (mode === 'profile' && sheet) {
      sheet.style.zoom = '1'
      const maxPx = ((297 - 14) * 96) / 25.4
      const height = sheet.scrollHeight
      if (height > maxPx) {
        sheet.style.zoom = String(Math.max(0.72, maxPx / height))
      }
    }

    const cleanup = () => {
      document.documentElement.classList.remove('emp-print-profile', 'emp-print-cv')
      document.body.classList.remove('emp-print-profile', 'emp-print-cv')
      if (sheet) sheet.style.zoom = ''
      pageStyle?.remove()
      window.removeEventListener('afterprint', cleanup)
    }
    window.addEventListener('afterprint', cleanup)
    window.print()
  }

  const handleBack = () => navigate('/employees')

  if (loading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack} className="no-print">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('employees.profile.back')}
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="mt-4 text-muted-foreground">{t('employees.profile.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack} className="no-print">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('employees.profile.back')}
        </Button>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">{t('employees.profile.notFound')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="employee-view-page space-y-4 bg-[#f3f6fb] -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 rounded-xl">
      <style>{`
        .employee-cv-print-root { display: none; }
        @media print {
          html.emp-print-profile, body.emp-print-profile,
          html.emp-print-cv, body.emp-print-cv {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          html.emp-print-profile .employee-cv-print-root,
          body.emp-print-profile .employee-cv-print-root { display: none !important; }
          html.emp-print-cv .employee-profile-page,
          body.emp-print-cv .employee-profile-page { display: none !important; }
          html.emp-print-cv .employee-cv-print-root,
          body.emp-print-cv .employee-cv-print-root { display: block !important; }
          html.emp-print-profile .employee-profile-page,
          body.emp-print-profile .employee-profile-page { display: block !important; }

          html.emp-print-profile, body.emp-print-profile {
            width: auto !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: #f3f6fb !important;
          }
          html.emp-print-profile #root,
          html.emp-print-profile main,
          html.emp-print-profile .min-h-screen {
            margin: 0 !important;
            padding: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            background: #f3f6fb !important;
            overflow: visible !important;
          }
          html.emp-print-profile .employee-view-page {
            margin: 0 !important;
            padding: 6px !important;
            min-height: 0 !important;
            height: auto !important;
            background: #f3f6fb !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
            max-width: none !important;
          }
          html.emp-print-profile * {
            max-width: none !important;
          }
          html.emp-print-profile .flex {
            flex-wrap: nowrap !important;
          }
          html.emp-print-profile .employee-profile-page .flex-wrap {
            flex-wrap: wrap !important;
          }
          html.emp-print-profile .employee-profile-page {
            display: flex !important;
            flex-direction: column !important;
            min-height: 277mm;
            gap: 12px !important;
          }
          html.emp-print-profile .employee-profile-page.space-y-5 > :not([hidden]) ~ :not([hidden]) {
            margin-top: 0 !important;
          }
          html.emp-print-profile .emp-profile-stack {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
            height: 100%;
          }
          html.emp-print-profile .emp-profile-stack.space-y-5 > :not([hidden]) ~ :not([hidden]) {
            margin-top: 0 !important;
          }
          html.emp-print-profile .emp-profile-stack .emp-profile-card {
            flex: 1 1 auto;
          }
          html.emp-print-profile .grid {
            gap: 12px !important;
          }
          html.emp-print-profile .employee-profile-hero {
            padding: 16px 18px !important;
            flex: 0 0 auto;
          }
          html.emp-print-profile .employee-profile-hero-grid {
            display: grid !important;
            grid-template-columns: 88px minmax(0, 1fr) 100px 200px !important;
            gap: 14px !important;
            align-items: center !important;
          }
          html.emp-print-profile .employee-profile-hero-grid > div:first-child {
            height: 88px !important;
            width: 88px !important;
          }
          html.emp-print-profile .employee-profile-hero h1 {
            font-size: 24px !important;
          }
          html.emp-print-profile .emp-profile-qr {
            height: 84px !important;
            width: 84px !important;
          }
          html.emp-print-profile .employee-profile-row {
            display: grid !important;
            grid-template-columns: 1fr 1fr 1fr !important;
            gap: 12px !important;
            flex: 1 1 0 !important;
            align-items: stretch !important;
            break-inside: auto !important;
            page-break-inside: auto !important;
          }
          html.emp-print-profile .emp-profile-card {
            padding: 14px 16px !important;
            height: 100%;
            break-inside: auto !important;
            page-break-inside: auto !important;
          }
          html.emp-print-profile .emp-profile-card h2 {
            font-size: 14px !important;
          }
          html.emp-print-profile .emp-profile-kv {
            padding-top: 6px !important;
            padding-bottom: 6px !important;
            gap: 8px !important;
          }
          html.emp-print-profile .emp-profile-kv span {
            font-size: 12px !important;
          }

          html.emp-print-cv, body.emp-print-cv {
            width: 210mm !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
          html.emp-print-cv #root,
          html.emp-print-cv main,
          html.emp-print-cv .min-h-screen,
          html.emp-print-cv .employee-view-page,
          html.emp-print-cv .employee-cv-print-root,
          html.emp-print-cv .cv-container {
            margin: 0 !important;
            padding: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            background: #fff !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          html.emp-print-cv .employee-view-page > * {
            margin: 0 !important;
          }
          html.emp-print-cv .emp-cv-sheet,
          html.emp-print-cv .emp-cv-sheet * {
            max-width: none !important;
          }
          html.emp-print-cv .emp-cv-sheet {
            width: 210mm !important;
            height: 297mm !important;
            min-height: 297mm !important;
            max-height: 297mm !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      <div className="flex items-center justify-between no-print">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('employees.profile.back')}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="bg-white" onClick={() => print('cv')}>
            <FileText className="w-4 h-4 mr-2" />
            {t('employees.profile.cv')}
          </Button>
          <Button className="bg-slate-800 hover:bg-slate-900" onClick={() => print('profile')}>
            <Printer className="w-4 h-4 mr-2" />
            {t('employees.profile.print')}
          </Button>
        </div>
      </div>

      <EmployeeProfileCards
        employee={employee}
        qrDataUrl={qrDataUrl}
        address={address}
        extraAddress={extraAddress}
        addressCountry={addressCountry}
        addressCity={addressCity}
        medical={medical}
        media={media}
        contract={contract}
        bank={bank}
        documents={documents}
        skills={skills}
        shiftLabel={shiftLabel}
        maritalName={maritalName}
        noticePeriodName={noticePeriodName}
        statusName={statusName}
      />

      <div className="employee-cv-print-root cv-container">
        <EmployeeCvLayout
          employee={employee}
          qualifications={qualifications}
          skills={skills}
          documents={documents}
          maritalName={maritalName}
          linkedin={media?.linkedInLink || media?.linkedinLink}
        />
      </div>
    </div>
  )
}
